import {
  Clip,
  Layout,
  MAX_CLIPS,
  clipTimeSec,
  notify,
  recomputeTimeline,
  state,
} from './state.ts';
import { detectFps } from './probe.ts';
import { Seeker, createSeeker, seekExact } from './seek.ts';
import { disposeFrameCache, initFrameCache, prefetch } from './frameCache.ts';

const VIDEO_RE = /\.(mp4|mov|m4v|webm)$/i;
const IMAGE_RE = /\.(png|jpe?g|webp|gif|bmp)$/i;

/** Minimum clip count per layout; used to filter the picker and auto-fallback. */
export const LAYOUT_MIN: Record<Layout, number> = {
  single: 1,
  wipe: 2,
  'side-by-side': 2,
  'top-bottom': 2,
  pip: 2,
  grid: 3,
  'dominant-right': 3,
  'dominant-bottom': 3,
};

let nextId = 1;
const seekers = new Map<string, Seeker>();

/** Last intake problem (unsupported file, undecodable codec) for the sidebar. */
export let intakeError: string | null = null;

function isVideoFile(f: File): boolean {
  return f.type.startsWith('video/') || VIDEO_RE.test(f.name);
}

function isImageFile(f: File): boolean {
  return f.type.startsWith('image/') || IMAGE_RE.test(f.name);
}

export async function addFiles(files: FileList | File[]): Promise<void> {
  intakeError = null;
  const usable = [...files].filter((f) => isVideoFile(f) || isImageFile(f));
  if (!usable.length && files.length) {
    intakeError = 'Unsupported file type — drop a video (mp4/mov/webm) or image (png/jpg/webp).';
    notify();
    return;
  }

  for (const file of usable) {
    if (state.clips.length >= MAX_CLIPS) {
      intakeError = `Clip limit reached (${MAX_CLIPS}) — remove one first.`;
      break;
    }
    const clip = isVideoFile(file) ? await loadVideo(file) : await loadImage(file);
    if (clip.error) {
      intakeError = `${file.name}: ${clip.error}`;
      URL.revokeObjectURL(clip.url);
      continue;
    }
    state.clips.push(clip);
    if (clip.video) seekers.set(clip.id, createSeeker(clip.video));
    recomputeTimeline();
    autoLayout(true);
    notify();
    void initFrameCache(clip); // background; falls back to <video> until ready
  }
  notify();
}

async function loadVideo(file: File): Promise<Clip> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';

  const clip: Clip = {
    id: `clip-${nextId++}`,
    kind: 'video',
    name: file.name,
    file,
    url,
    video,
    image: null,
    width: 0,
    height: 0,
    fps: 30,
    durationSec: 0,
    offsetFrames: 0,
    opacity: 1,
    thumb: null,
    error: null,
    scrubCache: 'off',
  };

  await new Promise<void>((resolve) => {
    video.addEventListener('loadedmetadata', () => resolve(), { once: true });
    video.addEventListener(
      'error',
      () => {
        clip.error = "couldn't decode — try an H.264 .mp4 (HEVC needs OS codec support).";
        resolve();
      },
      { once: true }
    );
    video.src = url;
    video.load();
  });

  if (!clip.error) {
    clip.width = video.videoWidth;
    clip.height = video.videoHeight;
    clip.durationSec = Number.isFinite(video.duration) ? video.duration : 0;
    if (!clip.width || !clip.durationSec) {
      clip.error = 'video has no decodable frames.';
    } else {
      clip.fps = await detectFps(video);
      await seekExact(video, 0, 1000);
      clip.thumb = captureThumb(video, clip.width, clip.height);
    }
  }
  return clip;
}

async function loadImage(file: File): Promise<Clip> {
  const url = URL.createObjectURL(file);
  const image = new Image();

  const clip: Clip = {
    id: `clip-${nextId++}`,
    kind: 'image',
    name: file.name,
    file,
    url,
    video: null,
    image,
    width: 0,
    height: 0,
    fps: 0,
    durationSec: 0,
    offsetFrames: 0,
    opacity: 1,
    thumb: null,
    error: null,
    scrubCache: 'off',
  };

  await new Promise<void>((resolve) => {
    image.addEventListener('load', () => resolve(), { once: true });
    image.addEventListener(
      'error',
      () => {
        clip.error = "couldn't decode this image.";
        resolve();
      },
      { once: true }
    );
    image.src = url;
  });

  if (!clip.error) {
    clip.width = image.naturalWidth;
    clip.height = image.naturalHeight;
    clip.thumb = captureThumb(image, clip.width, clip.height);
  }
  return clip;
}

function captureThumb(
  source: CanvasImageSource,
  sw: number,
  sh: number,
  w = 112,
  h = 63
): string | null {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    const scale = Math.min(w / sw, h / sh);
    const dw = sw * scale;
    const dh = sh * scale;
    ctx.drawImage(source, (w - dw) / 2, (h - dh) / 2, dw, dh);
    return canvas.toDataURL('image/jpeg', 0.6);
  } catch {
    return null;
  }
}

export function removeClip(id: string): void {
  const i = state.clips.findIndex((c) => c.id === id);
  if (i < 0) return;
  const [clip] = state.clips.splice(i, 1);
  seekers.get(id)?.dispose();
  seekers.delete(id);
  disposeFrameCache(id);
  if (clip.video) {
    clip.video.pause();
    clip.video.removeAttribute('src');
    clip.video.load();
  }
  URL.revokeObjectURL(clip.url);
  recomputeTimeline();
  autoLayout(false);
  notify();
}

export function moveClip(id: string, delta: number): void {
  const i = state.clips.findIndex((c) => c.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= state.clips.length) return;
  const [clip] = state.clips.splice(i, 1);
  state.clips.splice(j, 0, clip);
  recomputeTimeline();
  syncAllToFrame(state.frame);
  notify();
}

export function reorderClip(id: string, beforeId: string | null): void {
  const i = state.clips.findIndex((c) => c.id === id);
  if (i < 0) return;
  const [clip] = state.clips.splice(i, 1);
  const j = beforeId ? state.clips.findIndex((c) => c.id === beforeId) : state.clips.length;
  state.clips.splice(j < 0 ? state.clips.length : j, 0, clip);
  recomputeTimeline();
  syncAllToFrame(state.frame);
  notify();
}

export function swapFirstTwo(): void {
  if (state.clips.length < 2) return;
  [state.clips[0], state.clips[1]] = [state.clips[1], state.clips[0]];
  recomputeTimeline();
  syncAllToFrame(state.frame);
  notify();
}

export function setOffset(id: string, frames: number): void {
  const clip = state.clips.find((c) => c.id === id);
  if (!clip) return;
  clip.offsetFrames = Math.round(frames) || 0;
  recomputeTimeline();
  syncAllToFrame(state.frame);
  notify();
}

export function setOpacity(id: string, opacity: number): void {
  const clip = state.clips.find((c) => c.id === id);
  if (!clip) return;
  clip.opacity = Math.min(1, Math.max(0, opacity));
  notify();
}

/** Coalesced seek of one clip's video to the mapped time for a timeline frame. */
export function syncClipToFrame(clip: Clip, frame: number): void {
  if (!clip.video || clip.error) return;
  seekers.get(clip.id)?.seek(clipTimeSec(clip, frame));
  prefetch(clip); // warm the scrub cache around the new playhead
}

export function syncAllToFrame(frame: number): void {
  for (const clip of state.clips) syncClipToFrame(clip, frame);
}

/**
 * Keep the layout valid as clips come and go. Adding the 2nd clip jumps
 * straight to the wipe comparison (the tool's main use); dropping below a
 * layout's minimum falls back to the best layout for the new count.
 */
function autoLayout(added: boolean): void {
  const n = state.clips.length;
  if (added && n === 2 && state.layout === 'single') state.layout = 'wipe';
  if (n < LAYOUT_MIN[state.layout]) {
    state.layout = n >= 2 ? 'wipe' : 'single';
  }
}
